import React, { useState, useEffect, useRef } from 'react';
import { bookings } from '../../api';

/**
 * Required "who is joining" block for a booking request:
 *  - number of players (1..maxPlayers, no default - the player must choose)
 *  - one name per player. Each name can be picked from the search box
 *    (every registered player, not only the coach's own students) or simply
 *    typed when that person isn't on the app.
 *
 * value:  { count: '' | number, players: [{ name: string, userId: string|null }] }
 * onChange(nextValue)
 */
// One player books the lesson and may bring others: 60 min = you + 1, 90+ min = you + 3
export function maxPartyForSlot(slot) {
  const minutes = Math.round((new Date(slot.endTime) - new Date(slot.startTime)) / 60000);
  return minutes >= 90 ? 4 : 2;
}

export function isPartyValid(value, maxPlayers) {
  const count = Number(value.count);
  if (!Number.isInteger(count) || count < 1 || count > maxPlayers) return false;
  return value.players.length === count && value.players.every(p => p.name.trim().length > 0);
}

function NameField({ index, player, onChange, disabled, ownerName }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const reqId = useRef(0);

  // Debounced search; only while the field holds free text (not an already-picked account)
  useEffect(() => {
    const q = player.name.trim();
    if (!open || player.userId || q.length < 2) { setSuggestions([]); return undefined; }
    const id = ++reqId.current;
    const t = setTimeout(async () => {
      try {
        const res = await bookings.searchParticipants(q);
        if (id === reqId.current) setSuggestions(res.data || []);
      } catch (e) {
        if (id === reqId.current) setSuggestions([]);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [player.name, player.userId, open]);

  useEffect(() => {
    const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('touchstart', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('touchstart', close);
    };
  }, []);

  return (
    <div ref={boxRef} style={{ position: 'relative' }}>
      <input
        type="text"
        value={player.name}
        disabled={disabled}
        maxLength={80}
        placeholder={index === 0 ? (ownerName || 'Your name') : `Player ${index + 1} - search or type a name`}
        onFocus={() => setOpen(true)}
        onChange={(e) => { setOpen(true); onChange({ name: e.target.value, userId: null }); }}
        style={{
          width: '100%',
          boxSizing: 'border-box',
          padding: '8px 10px',
          border: `1px solid ${player.name.trim() ? '#ddd' : '#e0a0a0'}`,
          borderRadius: '4px',
          fontSize: '13px',
          fontFamily: 'inherit'
        }}
      />
      {player.userId && (
        <span style={{ position: 'absolute', right: 8, top: 8, fontSize: '11px', color: '#27ae60', fontWeight: 600 }}>
          ✓ on CoachPad
        </span>
      )}
      {open && suggestions.length > 0 && (
        <div style={{
          // in-flow (not absolute): the booking card clips overflow, which would cut a floating list off
          marginTop: 2,
          background: 'white', border: '1px solid #ddd', borderRadius: 4,
          boxShadow: '0 4px 12px rgba(0,0,0,0.12)', maxHeight: 180, overflowY: 'auto'
        }}>
          {suggestions.map(s => (
            <div
              key={s._id}
              role="button"
              tabIndex={0}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { onChange({ name: s.name, userId: s._id }); setOpen(false); setSuggestions([]); }}
              style={{ padding: '8px 10px', fontSize: '13px', cursor: 'pointer', color: '#2c3e50' }}
            >
              👤 {s.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function BookingPartyPicker({ value, onChange, maxPlayers, lessonMinutes, ownerName, disabled = false }) {
  const setCount = (raw) => {
    const n = Math.max(1, Math.min(maxPlayers, parseInt(raw, 10) || 1));
    const players = value.players.slice(0, n);
    while (players.length < n) {
      players.push(players.length === 0 ? { name: ownerName || '', userId: null } : { name: '', userId: null });
    }
    onChange({ count: n, players });
  };

  const count = Number(value.count) || 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <label style={{ fontSize: '13px', fontWeight: 600, color: '#2c3e50' }}>
        👥 How many players are joining? <span style={{ color: '#e74c3c' }}>*</span>
      </label>
      <div role="group" aria-label="Number of players" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {Array.from({ length: maxPlayers }, (_, i) => i + 1).map(n => {
          const selected = count === n;
          return (
            <button
              key={n}
              type="button"
              disabled={disabled}
              aria-pressed={selected}
              onClick={() => setCount(n)}
              style={{
                minWidth: 48, height: 44, padding: '0 14px', borderRadius: 10,
                fontSize: '16px', fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer',
                border: selected ? '2px solid #2f4ba2' : (count ? '1px solid #ddd' : '1px solid #e0a0a0'),
                background: selected ? 'linear-gradient(135deg, #2f4ba2 0%, #14246b 100%)' : 'white',
                color: selected ? 'white' : '#2c3e50',
                boxShadow: selected ? '0 4px 10px rgba(47, 75, 162, 0.35)' : 'none',
                transition: 'all 0.15s ease'
              }}
            >
              {n}
            </button>
          );
        })}
      </div>
      {maxPlayers > 0 && (
        <span style={{ fontSize: '11px', color: '#7f8c8d' }}>
          {`A ${lessonMinutes ? lessonMinutes + '-minute ' : ''}lesson fits up to ${maxPlayers} players: you + ${maxPlayers - 1} more`}
        </span>
      )}
      {count > 0 && value.players.map((p, i) => (
        <NameField
          key={i}
          index={i}
          player={p}
          disabled={disabled}
          ownerName={ownerName}
          onChange={(next) => onChange({
            ...value,
            players: value.players.map((q, j) => (j === i ? next : q))
          })}
        />
      ))}
    </div>
  );
}
